import React, {useEffect, useState} from 'react';
import {t} from 'i18next';
import {Typography, Box} from '@mui/material';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import './AddKeywords.scss';
import {ReactComponent as NoDataIcon} from '../../../../assets/images/no-data-found.svg';
import {ArrDrawer} from '../../../common/arr-drawer/ArrDrawer';
import {setAssignmentKeywordIds} from '../../../../redux/slice/assignmentSlice';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import {isEmpty} from '../../../../utils/formatters';

type AddKeywordsProps = {
  showDrawer: boolean;
  handleDrawer: (value: React.SetStateAction<boolean>) => void;
};

const AddKeywordsNoRowsOverlay = () => (
  <Box className="add-keywords-no-data-found">
    <NoDataIcon />
    <Typography
      className="typo-text headline3_size no-data-text"
      data-testid={`add-keywords-no-result-msg`}
    >
      {t('manageAssignments.addKeywords.noResultMsg')}
    </Typography>
  </Box>
);

const AddKeywords: React.FC<AddKeywordsProps> = ({
  showDrawer,
  handleDrawer,
}) => {
  const [selectedKeywords, setSelectedKeywords] = useState<string[] | null>();
  const [clearModal, setClearModal] = useState<boolean>(false);

  const lstKeywordsModel = useSelector(
    (state: RootState) => state.assignment.listData.lstKeywordsModel
  );

  const {assignmentKeywordIds, parentAssignmentGroupID} = useSelector(
    (state: RootState) => state.assignment.formData.assignmentModel
  );

  const treeData = useSelector(
    (state: RootState) => state.assignment.assignmentTree
  );
  const isParentCascaseEnabled = treeData?.find(
    (item) => +item.id === parentAssignmentGroupID
  )?.li_attr.isCascadeEnabled;

  const keywordsListUpdated =
    lstKeywordsModel?.map((i) => ({
      ...i,
      id: `${i?.keywordID}`,
      label: i?.keywordName,
      color: '#' + i?.keywordColorCode,
      disabled: isParentCascaseEnabled,
    })) ?? [];

  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedKeywords(assignmentKeywordIds);
  }, [assignmentKeywordIds]);

  const handleSave = () => {
    dispatch(setAssignmentKeywordIds(selectedKeywords));
    handleDrawer(false);
    setClearModal(false);
  };

  const handleCheckbox = (id: string) => {
    setSelectedKeywords((pre: any) => {
      if (!pre) return [id];

      return pre.includes(id)
        ? pre.filter((item: string) => item !== id)
        : [...pre, id];
    });
  };

  const contentBody = (
    <Box
      className={`add-keywords-container ${isEmpty(keywordsListUpdated) ? 'add-keywords-container-no-data' : ''}`}
    >
      <Box className="add-keywords-header-box">
        <Box className="title-box">
          <Typography
            className=" typo-text headline3_size title"
            data-testid="add-keywords-title"
          >
            {t('manageAssignments.addKeywords.title')}&nbsp;
          </Typography>
          <ArrTooltipWrapper
            trigger={
              <InfoIcon
                fontSize="medium"
                className="info-icon"
                data-testid="add-keywords-title-info"
              />
            }
          >
            {t('manageAssignments.addKeywords.titleInfo')}
          </ArrTooltipWrapper>
        </Box>
        <Box className="action-buttons-box">
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={() => {
              handleDrawer(false);
            }}
            disabled={false}
            dataTestId="add-keywords-cancel-button"
          >
            {t('common.cancel')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            onClick={() => setClearModal(true)}
            disabled={isParentCascaseEnabled || !keywordsListUpdated.length}
            dataTestId="add-keyword-save"
          >
            {t('common.save')}
          </ArrButton>
        </Box>
      </Box>
      {isEmpty(keywordsListUpdated) ? (
        <AddKeywordsNoRowsOverlay />
      ) : (
        <Box
          className={`content-box ${keywordsListUpdated?.length < 1 ? 'empty-state' : ''}`}
        >
          <Box className="checkbox-container">
            {keywordsListUpdated?.map((item, index) => {
              const isChecked = selectedKeywords?.includes(item.id) ?? false;
              return (
                <Box className="checkbox-label" key={item.id}>
                  <ArrCheckbox
                    checked={isChecked}
                    onChange={() => handleCheckbox(item?.id)}
                    data-testid={item?.id}
                    disabled={isParentCascaseEnabled}
                    aria-label={`Select-checkbox-${index}`}
                  ></ArrCheckbox>
                  <Typography
                    className="typo-text body3_size"
                    style={{color: `${item?.color}`}}
                    data-testid={`add-keywords-label-${index}`}
                  >
                    {item?.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {showDrawer && (
        <ArrDrawer
          showHeader={false}
          showButton={false}
          isOpen={showDrawer}
          size="small"
          side="right"
          onClose={() => handleDrawer(false)}
        >
          {contentBody}
        </ArrDrawer>
      )}
      {clearModal && (
        <ArrModal
          showModal={clearModal}
          onClose={() => setClearModal(false)}
          title={t('manageAssignments.addKeywords.addKeyword')}
          description={t('manageAssignments.addKeywords.addKeywordModalDesc')}
          showClose={false}
          dataTestId="add-keyword-modal"
          buttons={[
            {
              id: '1',
              label: t('common.no'),
              onClick: () => setClearModal(false),
              variant: 'secondary',
              dataTestId: 'akm-no-btn',
            },
            {
              id: '2',
              label: t('common.yes'),
              onClick: handleSave,
              variant: 'primary',
              dataTestId: 'akm-yes-btn',
            },
          ]}
        />
      )}
    </>
  );
};

export default React.memo(AddKeywords);
